import { useMemo } from 'react';
import classnames from 'classnames';
import { BiSolidCheckCircle } from 'react-icons/bi';
import { TruncatedWithTooltip } from '@ds';
import { GROUND_TRIP_CHECKPOINT_TYPE } from '@lp/features/RoutePlanner/config';
import { getTripLocationTitle } from '@lp/features/RoutePlanner/utils';
import { CheckpointModeType, CheckpointTypeEnum } from '@/api/apis/drive-service';
import { formatDate, convertMsToTimeString, TIME_WITH_TIMEZONE_FORMAT } from '@/utils/helpers';
import { MS_IN_S } from '@/constants/date.constants';
import { TripCheckpointProps } from './TripCheckpoint';
import { TripPassengers } from './TripPassengers';

type CompactTripCheckpointProps = Omit<TripCheckpointProps, 'departureDateTime' | 'arrivalDateTime'> & {
  type: 'route-list' | 'case-details';
};

export const CompactTripCheckpoint = ({ data, tripPassengers, type }: CompactTripCheckpointProps) => {
  const isPlannedCheckpoint = data.$type === CheckpointTypeEnum.PlannedCheckpointResponse;

  const passengersIds = useMemo(() => {
    switch (data.modeData.mode) {
      case CheckpointModeType.DropOff:
        return data.modeData.passengerIdsToDrop;
      case CheckpointModeType.PickUp:
        return data.modeData.passengerIdsToPickUp;
      default:
        return [];
    }
  }, [data]);

  const checkpointDateTimeString = useMemo(() => {
    switch (data.modeData.mode) {
      case CheckpointModeType.DropOff:
        return formatDate(data.modeData.dropOffDateTime, TIME_WITH_TIMEZONE_FORMAT, {
          timezone: data.modeData.ianaTimezone || 'UTC',
        });
      case CheckpointModeType.PickUp:
        return formatDate(data.modeData.pickUpDateTime, TIME_WITH_TIMEZONE_FORMAT, {
          timezone: data.modeData.ianaTimezone || 'UTC',
        });
      default:
        const dateTimeString = formatDate(data.modeData.waitStartTime, TIME_WITH_TIMEZONE_FORMAT, {
          timezone: data.modeData.ianaTimezone || 'UTC',
        });
        const waitTime = convertMsToTimeString(data.modeData.waitDurationInSeconds * MS_IN_S);

        return `${dateTimeString}, ${waitTime}`;
    }
  }, [data]);

  const locationString = getTripLocationTitle(data);

  return (
    <div className={classnames('relative w-full p-1 rounded-md', data.isCompleted ? 'bg-base-70' : 'bg-white')}>
      <div className={classnames('flex gap-2', { 'opacity-60': data.isCompleted })}>
        <span className="text-s-sb text-base-5">{GROUND_TRIP_CHECKPOINT_TYPE[data.modeData.mode]}</span>
        <div className="flex flex-col gap-1 min-w-0">
          <div className={classnames('flex gap-1', { 'flex-col': type === 'case-details' })}>
            <span className="text-s-m text-base-10 shrink-0">{checkpointDateTimeString}</span>
            <TruncatedWithTooltip
              value={locationString}
              valueClassName={classnames('text-s-r text-base-10', { 'max-w-85': type === 'route-list' })}
            />
          </div>
          {isPlannedCheckpoint && <TripPassengers type="compact" tripPassengers={tripPassengers} ids={passengersIds} />}
        </div>
      </div>
      {data.isCompleted && (
        <div className="absolute top-1 right-1 left-auto flex items-center text-[#0F8B99]">
          <BiSolidCheckCircle size={16} />
        </div>
      )}
    </div>
  );
};
